import { Alert, Button, Stack, TextField } from "@mui/material";
import { useState } from "react";
import { ApiError, changePassword } from "../../data/api";
import { bareMessage } from "../../data/data";
import { Failure, Field } from "../../ui/components/primitives";

// ---
// Change password, for a signed-in user. Needs the current password, so a hijacked session alone
// cannot lock the owner out; every other session is ended on success.
// ---

const MIN_LENGTH = 12;

export function ChangePassword(): React.JSX.Element {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const mismatch = confirm !== "" && confirm !== next;
  const tooShort = next !== "" && next.length < MIN_LENGTH;

  if (done) {
    return <Alert severity="success">Password changed. Other sessions have been signed out.</Alert>;
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        setError(null);
        void changePassword(current, next)
          .then(() => setDone(true))
          .catch((caught: unknown) => setError(bareMessage(caught)))
          .finally(() => setBusy(false));
      }}
    >
      <Stack spacing={4} sx={{ maxWidth: 360 }}>
        <Field label="Current password">
          <TextField
            type="password"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            required
            fullWidth
            autoComplete="current-password"
          />
        </Field>
        <Field label="New password" error={tooShort} hint={`At least ${MIN_LENGTH} characters`}>
          <TextField
            type="password"
            value={next}
            onChange={(event) => setNext(event.target.value)}
            required
            fullWidth
            autoComplete="new-password"
            error={tooShort}
          />
        </Field>
        <Field
          label="Confirm new password"
          error={mismatch}
          hint={mismatch ? "Passwords do not match" : " "}
        >
          <TextField
            type="password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            required
            fullWidth
            autoComplete="new-password"
            error={mismatch}
          />
        </Field>
        <Button
          type="submit"
          variant="outlined"
          sx={{ alignSelf: "flex-start" }}
          disabled={busy || mismatch || tooShort || current === "" || next === "" || confirm === ""}
        >
          {busy ? "Changing…" : "Change password"}
        </Button>
      </Stack>
      <Failure error={error} />
    </form>
  );
}
